import { html, css, nothing, type CSSResultGroup, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { IxitConfiguratorPreviewBase } from './configurator-preview-base.js';
import './quote-request-form.js';
import type { QuoteBlockingIssue } from './quote-request-form.js';
import './orphaned-inputs-box.js';
import { BOM_ARTICLE_NEEDS_PROJECT_EVENT, BOM_ARTICLES_ESTABLISHED_EVENT } from './bom-article-action.js';
import type { OrphanedInput, OrphanTarget } from './orphaned-inputs-box.js';
import './project-picker.js';
import type {
    ProjectCalculationChosen, ProjectCalculationSaved, ProjectCopySource, ProjectPickerMode,
    ProjectSaveGate,
} from './project-picker.js';
import './preview-view-menu.js';
import './restart-confirm-dialog.js';
import type { ConfirmPrompt } from './restart-confirm-dialog.js';
import './inline-name-editor.js';
import { inlineNameEditorStyles } from './inline-name-editor.js';
import type { EstablishedArticleLine, FieldAssumption, ProjectCalculationLine, WorkingDraftEntry } from '@ixit/dof-core';
import { picksFromAList } from '@ixit/dof-core';
import {
    markCalculationOpened, readProject, renameCalculation, renameProject,
} from '../projects-client.js';
import { CALCULATION_QUERY_KEY, DRAFT_QUERY_KEY, previewApiUrl, previewPageUrl } from '../preview-routes.js';
import {
    CalculationStore, discardDraft, draftEndpoints, listDrafts, projectCalculationEndpoints, renameDraft,
    isBlank, sameInputValue, sameInputs, storeDraftSummary, type CalculationProject, type DraftInputs,
} from '../working-calculation.js';
import { draftSummary, draftWhen } from '../draft-summary.js';
import {
    assumedFields, assumptionsStillHeld, type DraftAssumptions, type HeldAssumption,
} from '../utils/evaluation-envelope.js';

/**
 * The stream's state as the author sees it. `loggedOut` is the fourth, and unlike the other three it
 * is not a stream state at all: the backoffice session behind every request is gone, so nothing
 * will come back until the author logs in again (previewn tappar inloggningen tyst
 * (evalstack-integrity/57)). Measured: that session ends at login + TimeOut whatever the author
 * does, and the server answers 302 → /umbraco/login, never 401.
 */
type SseStatus = 'connected' | 'reconnecting' | 'disconnected' | 'loggedOut';

/**
 * The query-string name the schema generation travels under when a stream subscribes.
 *
 * The server owns the spelling: `PreviewSchemaGeneration.QueryKey` in
 * `Ixit.Dof.Backoffice/Infrastructure/PreviewSchemaGeneration.cs`, which is where it must be changed
 * first. It is a constant here rather than a literal in the URL for the same reason the migration
 * steps grew a `StepName` — a name spelled in two places drifts, and this one drifts silently: the
 * server simply stops seeing the parameter and every page looks current (review 2026-09-15).
 * Both sides pin the wire spelling in a test, so a rename that forgets the other half goes red.
 */
const SCHEMA_GENERATION_QUERY_KEY = 'g';

/** "Släng kladden…": what goes, and that nothing else does. */
const DISCARD_DRAFT_PROMPT: ConfirmPrompt = {
    headline: 'Släng kladden?',
    body: 'Kladden tas bort ur din lista med allt du matat in i den. Dina andra kladdar och dina '
        + 'projekt rörs inte. Det går inte att ångra.',
    confirmLabel: 'Släng kladden',
    cancelLabel: 'Avbryt',
    glyph: '✕',
};

/** Whether a dictionary holds an answer. A cleared field writes `''`, which answers nothing. */
function hasValues(inputs: DraftInputs): boolean {
    return Object.values(inputs).some((value) => value !== undefined && value !== null && value !== '');
}

/** A field the author types into, as this schema declares it. */
interface SchemaInputField {
    alias: string;
    label: string;
    dataType: string;
}

/**
 * Standalone preview component — the backoffice-free host for BOTH the author's
 * /configurator/preview/{id} page and the customer's public /kalkylator/{id} page. Reads its
 * initial schema and content id from window globals set by the Razor view, then evaluates.
 *
 * Features:
 * - SSE live reload: schema re-fetched when content is saved in backoffice (author only)
 * - localStorage input persistence: inputs survive schema reloads, F5, and browser-tab close/reopen
 * - Scroll position restored after schema swap
 * - Head row (author only): the live-connection status in words, and the draft's source links
 * - Saved values the schema has no field for are shown and offered a field (author only)
 * - `?preset=<slug>` deep-link, applied once on first load
 * - `?reopen=<token>` — the customer's own stored configuration, seeded by the Razor host (issue 05)
 *
 * `customer-mode` (the attribute the public host sets) is what separates the two: the base class
 * drops every author affordance and points the evaluation at the anonymous route; this class drops
 * the live-reload machinery, which exists for an author watching their own edits, and adds the
 * "begär offert" surface, which exists for a customer who wants an answer from a person.
 */
@customElement('ixit-configurator-preview-standalone')
export class IxitConfiguratorPreviewStandalone extends IxitConfiguratorPreviewBase {
    static styles: CSSResultGroup = [
        IxitConfiguratorPreviewBase.styles,
        inlineNameEditorStyles,
        css`
            /* The author's head row: what this page is, whether the stream that keeps it current is
               connected, and the draft's source links — ONE row in normal flow. It replaced two
               position:fixed elements that shared the top-right corner (the host page's link toolbar
               drew over the component's status badge, so the author never saw "reconnecting"), and
               an empty "Live Preview" tag in the base that read as a grey bar
               (den fristående previewn hör inte alltid en sparning — evalstack-integrity/56).
               Three groups, left to right: where I am, how it stands, how it is shown
               (projekt-i-kalkylatorn 18). */
            .live-head {
                display: flex;
                align-items: center;
                gap: 14px;
                /* The side spacing the configuration below has (.preview-container's padding), so
                   the row lines up with the content instead of running past it. */
                padding: 10px 20px;
                font-size: 0.8rem;
                color: var(--dof-muted-text);
            }
            .head-group {
                display: flex;
                align-items: center;
                gap: 8px;
                min-width: 0;
            }
            /* Where the author is takes the room that is left; the other two keep to the right end,
               in the order the row reads. */
            .head-where {
                flex: 1 1 auto;
            }
            .head-state {
                gap: 12px;
            }
            .live-title {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                min-width: 0;
                font-weight: 600;
                color: var(--uui-color-text);
            }
            .live-status {
                display: inline-flex;
                align-items: center;
                gap: 6px;
            }
            /* The WORD carries the status; the mark only repeats it, in colour AND form — a filled
               blue disc, a hollow orange ring, a grey square (Okabe-Ito, never red/green). */
            .live-dot {
                width: 8px;
                height: 8px;
                flex-shrink: 0;
                box-sizing: border-box;
                background: #9ca3af;
            }
            .live-status.connected .live-dot {
                border-radius: 50%;
                background: #0072b2;
                animation: pulse 2s infinite;
            }
            .live-status.reconnecting .live-dot {
                border-radius: 50%;
                background: transparent;
                border: 2px solid #e69f00;
            }
            /* Logged out: the row becomes a banner and STICKS to the top of the window, so it is in
               view however far down the author has scrolled. It differs from the three stream states
               in form as well as colour — a filled orange square, two sentences, a button — so it
               cannot be read as "reconnecting" with a different tint (evalstack-integrity/57).
               It lies over the sticky step header (10): with the session gone, nothing on the page
               matters more. A popover the author opens (50) still lies over it. */
            .live-head.logged-out {
                position: sticky;
                top: 0;
                z-index: 20;
                flex-wrap: wrap;
                padding: 10px 20px;
                margin: 0 0 10px;
                border: 2px solid #e69f00;
                border-radius: var(--uui-border-radius, 4px);
                background: var(--uui-color-surface);
                color: var(--uui-color-text);
                font-size: 0.85rem;
            }
            .live-head.logged-out .live-status.loggedOut .live-dot {
                width: 12px;
                height: 12px;
                border-radius: 0;
                background: #e69f00;
            }
            .live-head.logged-out .head-state,
            .live-head.logged-out .live-status {
                flex: 1 1 320px;
                gap: 10px;
                align-items: flex-start;
            }
            .live-head.logged-out .live-dot { margin-top: 3px; }
            /* The draft could not be saved. The WORDS carry it; the ⚠ and the border repeat it in
               form, and the colour is Okabe-Ito orange — never red against a green "saved". */
            .draft-unsaved {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 2px 8px;
                border: 1.5px solid #e69f00;
                border-radius: var(--uui-border-radius, 4px);
                color: var(--uui-color-text);
            }
            /* The project's other calculations, under the arrow. Anchored to the name it belongs
               to, and above the form, which scrolls under it. */
            .calc-menu {
                position: relative;
                display: inline-flex;
            }
            .calc-menu-list {
                position: absolute;
                top: 100%;
                left: 0;
                z-index: 3;
                display: flex;
                flex-direction: column;
                min-width: 240px;
                padding: 4px;
                border: 1px solid var(--uui-color-border);
                border-radius: var(--uui-border-radius, 4px);
                background: var(--uui-color-surface);
            }
            .calc-menu-item {
                padding: 6px 8px;
                border: none;
                background: none;
                color: var(--uui-color-text);
                font: inherit;
                text-align: left;
                cursor: pointer;
            }
            .calc-menu-item:hover:not(:disabled),
            .calc-menu-item:focus-visible {
                background: color-mix(in srgb, var(--uui-color-text) 8%, transparent);
            }
            .calc-menu-item:disabled {
                cursor: default;
                font-weight: 600;
            }
            /* Sets the destructive last row apart from the list of calculations. */
            .calc-menu-separator {
                height: 1px;
                margin: 4px 0;
                background: var(--uui-color-border);
            }
            /* When a draft was last written, and "ny" beside a draft not yet started: said, not
               stressed. */
            .draft-when,
            .draft-new {
                font-weight: 400;
                color: var(--dof-muted-text);
            }
            /* The address named a calculation the author cannot read, or "Ny kalkyl" could not reach
               the draft. Words and a ⚠ carry it; the orange only repeats them. */
            .calculation-refused,
            .new-calculation-refused {
                display: flex;
                gap: 8px;
                margin: 0 20px 10px;
                padding: 8px 10px;
                border: 1.5px solid #e69f00;
                border-radius: var(--uui-border-radius, 4px);
                color: var(--uui-color-text);
                font-size: 0.85rem;
            }
            /* The draft's source links, in the view menu: two ways to read the same schema, so they
               wrap rather than widen the panel. */
            .live-links {
                display: flex;
                flex-wrap: wrap;
                gap: 12px;
            }
            .live-links a {
                color: inherit;
                text-decoration: underline;
            }
            .live-links a:hover,
            .live-links a:focus-visible {
                color: var(--uui-color-text);
            }
            /* In the menu the controls stack: a menu is a list of choices, not a toolbar. */
            .view-menu-controls {
                display: flex;
                flex-direction: column;
                gap: 10px;
                font-size: 0.8rem;
            }
            .view-menu-controls .prefs-group {
                flex-wrap: wrap;
                margin-left: 0;
            }
            /* The server refused the new name, in its own words — beside the name it refused. */
            .name-refused {
                display: flex;
                gap: 8px;
                margin: 0 20px 10px;
                padding: 8px 10px;
                border: 1.5px solid #e69f00;
                border-radius: var(--uui-border-radius, 4px);
                color: var(--uui-color-text);
                font-size: 0.85rem;
            }
            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.4; }
            }
        `,
    ];

    /**
     * The deep link's OTHER form: `preset="<slug>"` on the element itself (presets-design §4 step 1
     * spells both, "URL eller web-component-attribut").
     *
     * <p>It exists because an embedded calculator has no query string of its own. On a marketing
     * product page the author has already said which grundinställning this section is about, and
     * that intent has nowhere else to travel — the page's URL belongs to the page.</p>
     */
    @property({ type: String }) preset = '';

    @state() private _sseStatus: SseStatus = 'disconnected';

    /**
     * The calculation on the server this surface answers to — the author's draft for this node, or
     * the project calculation the address names. Null on the public calculator, which has no author
     * and keeps localStorage (projekt-i-kalkylatorn 04, 07).
     */
    private _store: CalculationStore | null = null;

    /**
     * The project calculation the ADDRESS names (`?calculation=<streamId>`), or null for the draft.
     *
     * <p>The address is the mode: a reload, a link sent to oneself and the browser's back button all
     * land on the same page as the gesture that set it (projekt-i-kalkylatorn 07 point 3).</p>
     */
    private _calculationId: string | null = null;

    /**
     * The draft this page stands in (`?draft=<streamId>`), or null for a page that has none yet.
     *
     * <p>In the address as soon as the page has read or started a draft, because the id is what
     * keeps a tab in its draft across a reload once another tab has become the latest
     * (projekt-i-kalkylatorn 44).</p>
     */
    @state() private _draftId: string | null = null;

    /** The draft's own name, or null when the author has given none. */
    @state() private _draftName: string | null = null;

    /** The summary line the server holds for the draft, as far as this page knows. */
    @state() private _draftSummary: string | null = null;

    /** Whether the page knows what the server holds for the draft it stands in — its first read
     *  landed, or "Ny kalkyl" made it new. Until then its summary line is not overwritten, and a
     *  re-read is still the first read. */
    private _draftKnown = false;

    /** The author's drafts on this node, for the arrow's list; null until the menu has read them. */
    @state() private _drafts: WorkingDraftEntry[] | null = null;

    /** The address named a draft that is gone or not this author's, and the page fell back. */
    @state() private _draftRefused = false;

    /** Why "Släng kladden…" was refused, in the server's words, or null. */
    @state() private _discardRefused: string | null = null;

    /** Whether "Släng kladden…" is asking. */
    @state() private _discardAsking = false;

    /** Where the open calculation belongs, once the server has said so. Null in draft mode. */
    @state() private _project: CalculationProject | null = null;

    /** The open project's other calculations — what the arrow in the head row lists. */
    @state() private _projectCalculations: ProjectCalculationLine[] = [];

    @state() private _menuOpen = false;

    /** Which box is open, or null. */
    @state() private _picker: ProjectPickerMode | null = null;

    /**
     * An address named a calculation this author cannot read, and the page fell back to the draft.
     * Said out loud: a page that silently showed something else would be telling him he is in the
     * calculation he asked for.
     */
    @state() private _calculationRefused = false;

    /** Whether something the author did has not reached the server. Said in the head row, in
     *  words: a change that could not be saved must never look like one that was. */
    @state() private _draftUnsaved = false;

    /** A name change is on its way to the server. Both names wait for it: they are one row, and one
     *  of them is the project the other lives in. */
    @state() private _renaming = false;

    /** Why the server would not take the new name, in its own words, or null. */
    @state() private _nameRefused: string | null = null;

    /** Why "Ny kalkyl" stayed where it was, or null. */
    @state() private _newCalculationRefused: string | null = null;

    /**
     * What the first read of the calculation is doing. Exposed so a test — and the e2e spec — can
     * wait for the page to have adopted the server's answers instead of racing them.
     */
    _draftReady: Promise<void> | null = null;

    constructor() {
        super();
        // "Skapa artiklar" pressed in the draft: the save box is where a draft becomes a calculation
        // that may establish articles, so that is where the button leads.
        this.addEventListener(BOM_ARTICLE_NEEDS_PROJECT_EVENT, () => {
            this._picker = 'save';
            this._menuOpen = false;
        });
        // A run established or updated articles: the calculation is read again, so the box and the
        // project view name the numbers the calculation now owns.
        this.addEventListener(BOM_ARTICLES_ESTABLISHED_EVENT, () => { void this._rereadDraft(); });
    }

    /**
     * A value that arrived from the server while the author stood in the field it belongs to, per
     * alias: what the server said, and what was on screen at that moment. Kept rather than applied,
     * because a field must not change under the author's hands; applied on blur, and only if he has
     * not answered the field himself meanwhile — then his is the newer value.
     */
    private _deferredFromServer: Record<string, { server: unknown; localAtDefer: unknown }> = {};

    /** Draft reads, one at a time: two overlapping ones come back in either order, and the older
     *  answer must not be the one that lands. */
    private _draftWork: Promise<void> = Promise.resolve();

    /**
     * The saved values this schema has no field for, by alias — what the author is asked about
     * above the form, and the only place they live until that question is answered.
     *
     * <p>Never merged back into `inputs`: an alias the schema does not know would ride along in
     * every evaluation, which is precisely what the old silent filter was protecting against.</p>
     */
    @state() private _orphanedInputs: Record<string, any> = {};

    private _eventSource: EventSource | null = null;
    /**
     * The schema generation this page was rendered under — see connectedCallback — and null once it
     * has been handed over, because it travels on the FIRST subscribe only (see _connectSSE).
     */
    private _schemaGeneration: number | null = null;

    /** The generation a stream that has not opened yet was sent with; null once one has opened. */
    private _unspentGeneration: number | null = null;

    /** A refresh already running, so that a second signal joins it instead of racing it. */
    private _refreshInFlight: Promise<void> | null = null;
    private _refreshRequestedAgain = false;

    /**
     * The tab came back into view. Only worth a fetch when the stream was NOT there to tell us
     * what happened meanwhile: with a connected stream every save has already arrived as a
     * message, and a fetch per tab switch — the most frequent gesture in the authoring loop — would
     * be two schema builds and an evaluation for nothing.
     */
    private readonly _onVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        // Logged out: the author may just have logged in again in the other tab. Ask before
        // fetching, so the answer "still logged out" costs one redirect and no evaluation.
        if (this._sseStatus === 'loggedOut') { void this._tryResumeSession(); return; }
        if (this._sseStatus !== 'connected') void this._refreshFromServer();
    };

    /** The window got focus — the same question as visibility, for the tab that never left view. */
    private readonly _onFocus = () => {
        if (this._sseStatus === 'loggedOut') void this._tryResumeSession();
    };

    /** The pending stream reconnect, so a logged-out state can stop it. */
    private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    /** One session probe at a time: focus and visibility can fire together. */
    private _sessionProbe: Promise<boolean> | null = null;

    /**
     * Both of this class's surfaces get the way out of a configuration — the author's standalone
     * preview and the public calculator (`ett dolt fälts värde går inte att bli av med
     * (evalstack-integrity/36)`, whose triage put the exit on the public calculator too — a
     * configuration restored from last week has no other way out there either).
     *
     * <p>This is the class that PERSISTS: `_storageKey` is set in `connectedCallback` below, so a
     * leftover value here survives reloads and browser restarts and there is genuinely no other way
     * out of it. The base defaults the answer to false, so a subclass that persists nothing does
     * not inherit a destructive affordance by accident.</p>
     */
    protected override _offersStartOver(): boolean {
        return true;
    }

    /**
     * The author's page IS the window: nothing stands beside it, so the wrapper's 1400 px measure
     * only drew a white frame around the calculator on a wide screen
     * (projekt-i-kalkylatorn 18 points 1–2). The customer's calculator keeps the measure — that one
     * is a reading surface.
     */
    protected override _fillsWindow(): boolean {
        return !this.customerMode;
    }

    /**
     * No loose toolbar here: the view controls are in the head row's menu, where the rest of this
     * page's affordances are (projekt-i-kalkylatorn 18 points 3–4). The base renders them; this
     * class decides where.
     */
    protected override _rendersViewToolbar(): boolean {
        return false;
    }

    connectedCallback() {
        super.connectedCallback();
        const w = window as any;
        this._contentId = w.DOF_CONTENT_ID ?? '';
        const storageKey = `preview:inputs:${this._contentId}`;
        this._storageKey = storageKey;

        this.schema = w.DOF_SCHEMA ?? null;
        // Which generation the schema in this page was built under. The stream compares it on
        // subscribe: a page RENDERED before a deploy dropped the generation and connected after it
        // would otherwise never hear, because the first open deliberately does not refetch and the
        // signal it missed is not replayed (en rå innehållsskrivning invaliderar inte cachen,
        // evalstack-integrity/59).
        //
        // null when this page carries no generation — older HTML, a cached page, a host that sets
        // no global — and then the parameter is left OFF the URL entirely. Sending a zero would be
        // the opposite of what the server's rule says: it parses, it is strictly older than every
        // live generation, and every such page would be handed a pointless refetch. "Unparseable
        // means silent" has to mean absent too (review 2026-09-15).
        this._schemaGeneration =
            Number.isInteger(w.DOF_SCHEMA_GENERATION) ? w.DOF_SCHEMA_GENERATION as number : null;

        // What an earlier load already asked about and the author has not answered yet. Read BEFORE
        // the inputs, so this load's own leftovers merge into it rather than replace it.
        this._restoreOrphans();

        // Restore saved inputs before first evaluate, but keep out of `inputs` the aliases that no
        // longer exist as user-input fields in the schema. Without this, stale entries
        // from a previous schema-shape persist forever — e.g. a CalcTable block alias
        // that used to be a regular dofField shows up as "tidsAtgang: ''" in the
        // Inputs payload long after the field was promoted to a CalcTable.
        // localStorage primary; fall back to sessionStorage for any pre-switch
        // state so we don't lose work users had open when this code shipped.
        const saved = localStorage.getItem(storageKey)
            ?? sessionStorage.getItem(storageKey);
        let storedInputs: DraftInputs = {};
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                storedInputs = parsed;
                const { kept, orphaned } = this._splitStoredInputs(parsed, this.schema);
                this.inputs = kept;
                this._rememberOrphans(orphaned);
            } catch { /* ignore corrupt data */ }
        }

        // Restore the active preset selection (persisted by _persistInputs) so the
        // "switching overwrites your edits" confirm survives a reload — without it the selection is
        // lost and a post-reload preset switch silently overwrites the restored inputs.
        //
        // Through the base's accessor, not a hand-built `${key}:presets`. The suffix is the base's
        // to spell: it writes that key, `_discardConfiguration` removes it, and this reads it — a
        // fourth spelling here is exactly how the three call sites got out of step in the first
        // place.
        const presetsKey = this._presetsStorageKey;
        const savedPresets = presetsKey ? localStorage.getItem(presetsKey) : null;
        if (savedPresets) {
            try { this._selectedPreset = JSON.parse(savedPresets); } catch { /* ignore corrupt data */ }
        }

        // AFTER the restored inputs, so a link wins over whatever this browser had lying around —
        // the link is the more recent intent, and a product page sending a customer to "3-steg" that
        // silently kept last week's 2-steg is the bug.
        //
        // A REOPEN link outranks a preset one, and then the preset is not applied at all: ?preset=
        // is where a configuration STARTS, ?reopen= is a configuration the customer already made and
        // we mailed back to them. Applying the preset afterwards would discard exactly what the link
        // promised to restore, and applying it first would leave the picker highlighting a card the
        // inputs no longer match.
        if (!this._applyReopenSeed()) this._applyPresetFromUrl();

        if (this.schema) this._evaluate();
        // Live reload is an author watching backoffice saves land. A customer's page renders one
        // PUBLISHED configurator; there is nothing to watch, and the SSE endpoint is auth-gated
        // anyway — connecting would be a reconnect loop against a 401.
        if (!this.customerMode) {
            // Which calculation this page is, read from the address BEFORE the first store is built:
            // the endpoints differ, and a page that started as the draft and became a project
            // calculation afterwards would have written one event into the wrong stream.
            this._calculationId = IxitConfiguratorPreviewStandalone._idFromAddress(CALCULATION_QUERY_KEY);
            this._draftId = this._calculationId
                ? null
                : IxitConfiguratorPreviewStandalone._idFromAddress(DRAFT_QUERY_KEY);
            // The server is the store from here on. The read happens AFTER the localStorage restore
            // above, not instead of it: the page has something to draw while the request is in the
            // air, and what this browser had is what the one hand-over below sends if the author has
            // no draft yet.
            this._store = this._newStore();
            this._draftReady = this._adopt({ ...this._orphanedInputs, ...storedInputs });
            // A page that does not know its draft yet subscribes again once the read has named it
            // (`_onDraftNamed`): the channel is per draft, and a subscription naming none hears no
            // calculation.
            this._connectSSE();
            // A signal missed while this tab lay behind the backoffice with its stream down is
            // caught up the moment the author looks (den fristående previewn hör inte alltid en
            // sparning — evalstack-integrity/56).
            document.addEventListener('visibilitychange', this._onVisibilityChange);
            window.addEventListener('focus', this._onFocus);
        }
    }

    /**
     * The configuration a reopen link carries (publik-webbplats-kalkylator issue 05), or nothing.
     *
     * <p><b>The server put it there.</b> `?reopen=<token>` is read by the Razor host, which looks the
     * request up and writes its stored inputs into the document — the same way the schema arrives,
     * for the same reason: one request, no empty frame, and the token never has to be presented a
     * second time from script. There is no client-side fetch to build and no second endpoint to gate;
     * a page that renders at all is a page whose token was accepted.</p>
     *
     * <p><b>No translation.</b> What is written here is the engine's own input dictionary, stored
     * verbatim when the customer submitted it — so the seed is an assignment, not a mapping. That is
     * what issue 04's replay-aware storage was for, and a shape conversion appearing here would mean
     * it had stopped being true.</p>
     *
     * <p>Split against the schema exactly as the localStorage restore is: a configurator that has
     * been re-authored since the submission may no longer have the field an input names, and a stale
     * alias would ride along in every evaluation and back into the next quote request. On the
     * author's surface the leftovers are shown; on the customer's they are dropped, as
     * `_rememberOrphans` says.</p>
     *
     * <p>The seed REPLACES what this browser had, and the active preset selection goes with it: a
     * stored configuration was not produced by a preset we can name, and keeping a highlighted card
     * that the inputs no longer match would be the picker telling the customer something untrue.</p>
     */
    private _applyReopenSeed(): boolean {
        const seed = (window as any).DOF_REOPEN_INPUTS;
        if (!seed || typeof seed !== 'object' || Array.isArray(seed)) return false;

        const { kept, orphaned } = this._splitStoredInputs(seed as Record<string, any>, this.schema);
        this.inputs = kept;
        this._rememberOrphans(orphaned);
        this._selectedPreset = {};
        // Persisted immediately, so this tab and the localStorage it shares with the customer's other
        // tabs agree about what is on screen. Without it, opening the calculator again WITHOUT the
        // token would silently bring back the configuration the reopen link just replaced.
        this._persistInputs();
        return true;
    }

    /**
     * `?preset=<slug>` — the entry point a product page's calculator link uses (presets-design §4).
     *
     * §4 is explicit that a deep link does TWO things: the matched preset's parent tool gives the
     * leaf (`toolType`), and its structure seeds the inputs. Seeding alone would land the customer on
     * the tool picker with an invisible configuration behind it, which is not what a product page's
     * "räkna på den här borren" link promises.
     *
     * Structure is seeded here, synchronously, so the very first evaluation already carries it. The
     * LEAF has to wait: the option list a dropdown offers is EVALUATED (an author may filter it), so
     * it is not on the render schema at all — see _resolveDeepLinkLeaf, which runs when the first
     * state lands.
     *
     * <p>Applied through the SAME path the preset picker uses, so the two cannot drift into applying
     * a preset two different ways. An unknown slug does nothing at all, deliberately: a stale link on
     * a product page should open the calculator, not an error page.</p>
     *
     * <p><b>URL beats attribute.</b> The attribute is the author's standing choice for an embedded
     * calculator; a `?preset=` is a link somebody was sent, right now, to THIS page. When both are
     * present the more recent intent is the one in the address bar — the same reasoning that makes
     * a deep link beat localStorage below.</p>
     */
    private _applyPresetFromUrl() {
        const slug = new URLSearchParams(window.location.search).get('preset') || this.preset || null;
        if (!slug) return;

        const found = this._findPresetBySlug(slug, this.schema?.steps ?? []);
        if (!found) return;

        // 'overwrite', not 'confirm': the link IS the intent, and it arrives before the customer has
        // looked at the page — a blocking dialog on load is no way to ask. It therefore wins over
        // whatever this browser had in localStorage, which is the point of sending someone a link to
        // "3-steg" rather than to the calculator. (The in-page picker keeps 'confirm': there the
        // customer is looking at the values they are about to lose.)
        void this._applyPreset(found.item, slug, found.preset, 'overwrite');
        this._pendingDeepLinkSlug = slug;
    }

    /**
     * The deep link's slug, held until its LEAF has been set. Cleared either way on the first
     * resolution attempt — a link that cannot name a tool must not keep trying on every render.
     */
    private _pendingDeepLinkSlug: string | null = null;

    protected updated(changed: PropertyValues) {
        super.updated(changed);
        // The evaluated state is the first thing that carries dropdown OPTIONS, so it is the
        // earliest moment the leaf can be resolved.
        //
        // `this.state` must be truthy, not merely present in changedProperties. Lit reports a
        // field-initialised property as changed on the FIRST update, so the very first render —
        // which happens long before the evaluation returns — arrives here with `state: null`. Acting
        // on that consumed the pending slug against an empty tree and the leaf was never set: the
        // customer landed on the tool picker with a seeded configuration hidden behind it, which is
        // the whole bug this resolution exists to fix.
        if (this._pendingDeepLinkSlug && changed.has('state') && this.state) this._resolveDeepLinkLeaf();
        // After the update rather than inside it: storing the line changes what the head shows.
        if (changed.has('state')) queueMicrotask(() => this._storeSummaryIfChanged());
        if (changed.has('state')) this._storeAssumedChoices();
    }

    /**
     * Sets the tool leaf the deep-linked preset belongs to (presets-design §4: "dess förälder-verktyg
     * ger toolType").
     *
     * <p><b>Why it matches on options rather than reading a formula.</b> What makes a branch open is
     * the step's visibility formula (`toolType == "stepDrill"`), and that formula is deliberately not
     * on the public wire. What IS on the wire is the leaf-selector's evaluated option list, and the
     * plugin item's own <c>itemType</c> — and the tool a preset belongs to is exactly the option
     * naming that item type. So the resolution is: find the field offering an option whose value is
     * this item's type, and select that option BY ITS OWN VALUE, never by the itemType string. The
     * two differ in case ("stepdrill" vs "stepDrill") and the engine compares strings exactly;
     * writing the itemType would select a tool the branch does not recognise.</p>
     *
     * <p>Nothing found means nothing happens: a configurator with no leaf selector (one product, no
     * branch) is a perfectly ordinary shape, and the seed already landed.</p>
     */
    private _resolveDeepLinkLeaf() {
        const slug = this._pendingDeepLinkSlug;
        this._pendingDeepLinkSlug = null;
        if (!slug) return;

        const itemType = this._findPresetBySlug(slug, this.schema?.steps ?? [])?.item?.itemType;
        if (!itemType) return;

        const leaf = this._findLeafOptionFor(itemType, (this.state as any)?.steps ?? []);
        if (!leaf) return;
        if (String(this.inputs[leaf.alias] ?? '') === String(leaf.value)) return;

        this._onInputChange(leaf.alias, leaf.value, 'Dropdown');
        this._onFieldSettled(leaf.alias);
    }

    /** The first evaluated field offering an option that names this item type, and that option's own
     *  value. Case-insensitive on the way IN, exact on the way out. */
    private _findLeafOptionFor(
        itemType: string,
        steps: any[],
    ): { alias: string; value: unknown } | undefined {
        for (const step of steps) {
            for (const field of step.fields ?? []) {
                const hit = (field.options ?? []).find(
                    (o: any) => String(o?.value ?? '').toLowerCase() === itemType.toLowerCase());
                if (hit) return { alias: field.alias, value: hit.value };
            }
            const nested = this._findLeafOptionFor(itemType, step.steps ?? []);
            if (nested) return nested;
        }
        return undefined;
    }

    /** Depth-first walk for the schema item carrying a preset with this slug. */
    private _findPresetBySlug(slug: string, steps: any[]): { item: any; preset: any } | undefined {
        for (const step of steps) {
            for (const item of step.items ?? []) {
                const preset = (item.presets ?? []).find((p: any) => p.slug === slug);
                if (preset) return { item, preset };
            }
            const nested = this._findPresetBySlug(slug, step.steps ?? []);
            if (nested) return nested;
        }
        return undefined;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._eventSource?.close();
        this._eventSource = null;
        if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        window.removeEventListener('focus', this._onFocus);
    }

    /** The fields an author types into, by alias, plus the plugin items that own their own input
     *  shape (Stepdrill: a segment array). Items are not dataType-classified because they are not
     *  Fields, and their alias-keyed input must survive a refresh like any other answer. */
    private _collectSchemaInputs(schema: any): {
        fields: Map<string, SchemaInputField>;
        itemAliases: Set<string>;
    } {
        const fields = new Map<string, SchemaInputField>();
        const itemAliases = new Set<string>();
        const collectFromStep = (s: any) => {
            for (const f of s.fields ?? []) {
                // Only Numeric / a list / Text are user inputs; Result and CalcTable aliases are
                // server-computed.
                if (f.dataType === 'Numeric' || picksFromAList(f.dataType) || f.dataType === 'Text') {
                    if (f.alias) {
                        fields.set(f.alias, {
                            alias: f.alias, label: f.label || f.alias, dataType: f.dataType,
                        });
                    }
                }
            }
            for (const it of s.items ?? []) {
                if (it.alias) itemAliases.add(it.alias);
            }
            for (const sub of s.steps ?? []) collectFromStep(sub);
        };
        for (const s of schema.steps ?? []) collectFromStep(s);
        return { fields, itemAliases };
    }

    /**
     * The stored configuration split into what this schema still has a field for and what it has
     * not — and the leftovers are RETURNED rather than dropped.
     *
     * <p>Dropping them silently is the defect this answers: rename `diameter` to `skaftDiameter`
     * while developing a formula and the six the author typed is gone on the next load, with the new
     * field blank and nothing said (`sparade värden vars fält försvunnit visas`,
     * projekt-i-kalkylatorn 01). Nothing orphaned may reach an evaluation, which is why the two
     * halves are separated here rather than filtered later.</p>
     *
     * <p>A dotted key is never an outer field alias — "." is not a legal identifier character — and
     * is kept whatever the walk above reached: plugin state (`stepDrill._mode`), and the answer to an
     * inner calculator's asked field (`amne.langd`), which the evaluation reports on the block rather
     * than in this schema. It is therefore never an orphan either: the author has no field to move it
     * to, and whoever owns the prefix ignores a key it does not know.</p>
     */
    private _splitStoredInputs(
        stored: Record<string, any>,
        schema: any,
    ): { kept: Record<string, any>; orphaned: Record<string, any> } {
        if (!schema?.steps) return { kept: stored, orphaned: {} };
        const { fields, itemAliases } = this._collectSchemaInputs(schema);
        const kept: Record<string, any> = {};
        const orphaned: Record<string, any> = {};
        for (const [k, v] of Object.entries(stored)) {
            if (fields.has(k) || itemAliases.has(k) || k.includes('.')) kept[k] = v;
            else orphaned[k] = v;
        }
        return { kept, orphaned };
    }

    /**
     * Holds on to what the split left over, on the author's surface only.
     *
     * <p>The public calculator keeps today's behaviour and drops it: the box names the author's own
     * field aliases, and a customer must not be shown the inside of a configurator. The page's own
     * gate is the same one — `/configurator/preview/{id}` is `[Authorize]`d (ConfiguratorPreviewController),
     * the public host is anonymous and marks itself with `customer-mode`.</p>
     */
    private _rememberOrphans(orphaned: Record<string, any>): void {
        if (this.customerMode || Object.keys(orphaned).length === 0) return;
        this._orphanedInputs = { ...this._orphanedInputs, ...orphaned };
        this._persistOrphans();
    }

    /**
     * What an earlier load already set aside, verbatim.
     *
     * <p>Not re-split against the schema: an alias that has come back as a field is not thereby the
     * author's answer to it, and adopting it would overwrite whatever they have typed into the new
     * field since. An orphan leaves this list only when the author moves or discards it.</p>
     */
    private _restoreOrphans(): void {
        const key = this._orphanedStorageKey;
        if (this.customerMode || !key) return;
        const saved = localStorage.getItem(key);
        if (!saved) return;
        try {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                this._orphanedInputs = parsed as Record<string, any>;
            }
        } catch { /* ignore corrupt data */ }
    }

    /** Only where the box is a list of its own. On the signed-in surface the leftovers live in the
     *  stream, and a second copy in this browser would be the one that goes stale. */
    private _persistOrphans(): void {
        const key = this._orphanedStorageKey;
        if (!key || this._draftBacked) return;
        try {
            if (Object.keys(this._orphanedInputs).length === 0) localStorage.removeItem(key);
            else localStorage.setItem(key, JSON.stringify(this._orphanedInputs));
        } catch { /* private mode — nothing was stored to begin with */ }
    }

    /**
     * The fields an orphaned value can be moved into: the ones the author types in that have no
     * value today.
     *
     * <p>Only the empty ones, because a move is an assignment and an assignment onto an answered
     * field would destroy that answer with nothing to undo it — the field the author meant is the
     * one standing empty where the renamed one used to be.</p>
     */
    private _orphanTargets(): OrphanTarget[] {
        const { fields } = this._collectSchemaInputs(this.schema);
        const targets: OrphanTarget[] = [];
        for (const field of fields.values()) {
            const value = this.inputs[field.alias];
            if (value === undefined || value === null || value === '') {
                targets.push({ alias: field.alias, label: field.label });
            }
        }
        return targets;
    }

    /**
     * The author says which field the value belongs to — the same path a typed value takes
     * (`_onInputChange`), so the re-evaluation follows and the move is not a special case anywhere
     * downstream.
     */
    private _moveOrphan(alias: string, target: string): void {
        if (!(alias in this._orphanedInputs)) return;
        const dataType = this._collectSchemaInputs(this.schema).fields.get(target)?.dataType;
        if (!dataType) return;
        this._onInputChange(target, this._orphanedInputs[alias], dataType);
        this._onFieldSettled(target);
        this._forgetOrphan(alias);
    }

    /**
     * The row leaves the box and the stored configuration both.
     *
     * <p>The inputs key is rewritten as well, and that is not redundant: until some input changes it
     * still holds the copy this load's split read the orphan OUT of, so a discard that only touched
     * the orphan key would be undone by the next reload.</p>
     *
     * <p>Where the draft is the store the same act is an `InputChanged` with null for the alias —
     * the stream's own way of saying the value is gone, since the fold replays everything that was
     * ever said and a row removed only here would come back with it.</p>
     */
    private _forgetOrphan(alias: string): void {
        const rest = { ...this._orphanedInputs };
        delete rest[alias];
        this._orphanedInputs = rest;
        this._persistOrphans();
        this._persistInputs();
        void this._store?.setInput(alias, null);
    }

    // -------------------------------------------------------------------------------------------
    // The draft on the server — projekt-i-kalkylatorn 04
    // -------------------------------------------------------------------------------------------

    /**
     * Whether the author's draft on the server is this page's store.
     *
     * <p>Asked of the MODE and not of whether `_store` has been built yet: the answer has to hold
     * from the first line of `connectedCallback`, where the restore runs, and a getter that turned
     * true halfway through it would let that restore write a key the surface no longer keeps.</p>
     */
    private get _draftBacked(): boolean {
        return !this.customerMode;
    }

    /** The numbers the open calculation owns, for the box to tell an update from a new number. */
    protected override bomArticleEstablished(): EstablishedArticleLine[] {
        return this._project?.articles ?? [];
    }

    /** The stream "Skapa artiklar" writes into: the project calculation once the server has confirmed
     *  it, never the draft, which may not establish articles (projekt-i-kalkylatorn 33). */
    protected override bomArticleStreamId(): string | null {
        return this._project ? this._calculationId : null;
    }

    /** The answers live in the author's draft on the server, so this browser stops keeping a second
     *  copy of them. The public calculator has no author and keeps localStorage. */
    protected override _persistsInputsLocally(): boolean {
        return !this._draftBacked;
    }

    /**
     * The store this page stands in: the project calculation the address names, or a draft of this
     * node's — the one named, or, without an id, the latest on the first read and a new one on the
     * first write.
     *
     * <p>A store that is no longer the page's does not move the page: an answer that arrives for a
     * draft the page has just left says nothing about where the page is.</p>
     */
    private _newStore(): CalculationStore {
        if (this._calculationId) {
            return new CalculationStore(projectCalculationEndpoints(this._calculationId), () => {
                this._draftUnsaved = this._store?.unsaved ?? false;
            });
        }
        const store: CalculationStore = new CalculationStore(
            draftEndpoints(this._contentId, this._draftId, (streamId) => {
                if (this._store === store) this._onDraftNamed(streamId);
            }),
            () => { this._draftUnsaved = this._store?.unsaved ?? false; },
        );
        return store;
    }

    /**
     * The server named the draft this page stands in — the first read found the latest, or the first
     * write started one. Into the address, so a reload comes back to it; onto the event stream, which
     * heard no calculation while the page had none; and the summary is stored, since a draft that was
     * just started has none yet.
     */
    private _onDraftNamed(streamId: string): void {
        if (streamId === this._draftId) return;
        const started = this._draftId === null && this._draftKnown;
        this._draftId = streamId;
        this._writeAddress();
        if (this._eventSource) this._connectSSE();
        if (started) this._storeSummaryIfChanged();
    }

    /** The id the address carries under this key, or null. A value that is not a stream id at all is
     *  null too: a mistyped link opens the latest draft rather than an error page. */
    private static _idFromAddress(key: string): string | null {
        const value = new URLSearchParams(window.location.search).get(key);
        return value && /^[0-9a-f-]{36}$/i.test(value) ? value : null;
    }

    /**
     * The address says which calculation or draft the page stands in — `replaceState`, not a
     * navigation: the page IS it already, and a reload or a shared link must land on it.
     */
    private _writeAddress(): void {
        const url = new URL(window.location.href);
        if (this._calculationId) url.searchParams.set(CALCULATION_QUERY_KEY, this._calculationId);
        else url.searchParams.delete(CALCULATION_QUERY_KEY);
        if (!this._calculationId && this._draftId) url.searchParams.set(DRAFT_QUERY_KEY, this._draftId);
        else url.searchParams.delete(DRAFT_QUERY_KEY);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    /**
     * The first read: what the server has wins, with one exception, once.
     *
     * <p><b>The exception</b> is the browser that had answers before this surface had a draft at
     * all. Its dictionary is handed over as a single `InputsCaptured` — one transfer, at one
     * instant, which is what actually happened — and the localStorage keys are removed, so the
     * hand-over cannot happen a second time. A stream that EXISTS is never handed anything on load,
     * however old this browser's copy is: that is the whole of "annars vinner servern, utan
     * fråga". A PROJECT calculation is never handed anything either, whatever this browser holds:
     * it is a calculation somebody saved, not a scratch pad.</p>
     *
     * <p>An unreachable server leaves the page as it stands and says so in the head row, rather
     * than blanking what the author is looking at. A calculation or draft the server says is not
     * there — another author's, one lifted out of its project, a draft thrown away — is a different
     * answer, and the page falls back to the latest draft and says so.</p>
     */
    private async _adopt(handover: DraftInputs): Promise<void> {
        const store = this._store;
        if (!store) return;

        const outcome = await store.read();
        if (outcome === 'loggedOut') { this._onSessionLost(); return; }
        // A page that could not even READ the calculation cannot write one either, and what it is
        // showing is this browser's old copy rather than the answer. Said in the same words, and
        // taken back by the first read that does get through.
        if (outcome === 'unreachable') { this._draftUnsaved = true; return; }
        if (outcome === 'notFound') {
            // The read of the LATEST draft answers "none" with a 200, so only a named calculation or
            // draft gets here; without a name there is nothing to fall back from, and it would loop.
            if (!this._calculationId && !this._draftId) { this._draftUnsaved = true; return; }
            await this._fallBackToDraft(handover);
            return;
        }
        this._draftUnsaved = store.unsaved;

        if (this._calculationId) {
            this._project = outcome.project;
            // An empty project calculation is a real state, so this is applied unconditionally —
            // unlike a draft that does not exist yet.
            this._applyDraftInputs(outcome.inputs, outcome.assumptions);
            void markCalculationOpened(this._calculationId);
            void this._loadProjectCalculations();
            return;
        }

        this._draftName = outcome.name;
        this._draftSummary = outcome.summary;
        this._draftKnown = true;
        if (outcome.streamId) { this._applyDraftInputs(outcome.inputs, outcome.assumptions); return; }
        if (Object.keys(handover).length === 0) return;

        await store.captureAll(handover);
        this._forgetLocalInputs();
    }

    /**
     * The address named a calculation or draft this author may not read: say so, drop it from the
     * address, and show the node's latest draft (projekt-i-kalkylatorn 07 point 3).
     *
     * <p>The stream is re-subscribed as well: it was asked to watch something the server refused, and
     * a page listening for a wake-up that can never come would look connected and be deaf.</p>
     */
    private async _fallBackToDraft(handover: DraftInputs): Promise<void> {
        if (this._calculationId) this._calculationRefused = true;
        else this._draftRefused = true;
        this._calculationId = null;
        this._project = null;
        this._projectCalculations = [];
        this._draftId = null;
        this._writeAddress();
        this._store = this._newStore();
        this._connectSSE();
        await this._adopt(handover);
    }

    /** The open project's calculations, for the arrow in the head row. A list the page already has
     *  the right to read — it is the project this calculation is in. */
    private async _loadProjectCalculations(): Promise<void> {
        const projectId = this._project?.id;
        if (!projectId) return;
        const contents = await readProject(projectId);
        if (this._project?.id === projectId) this._projectCalculations = contents?.calculations ?? [];
    }

    /**
     * What a write into a project waits for: everything the author did reaching the SERVER first.
     *
     * <p>For "Spara i projekt" the move keeps the stream, but the store is swapped for one on the
     * project's endpoints the moment it is done — so a field POST still queued or in flight would be
     * sent to the draft the page has just left, re-creating it, and the next read would overwrite
     * `inputs` with the server's older dictionary. For "Kopiera till…" the copy is seeded from what
     * the source stream folds to on the server, so the same field would simply not be in the copy.
     * Either way the last field the author filled before clicking would be gone.</p>
     *
     * <p>Writes that could not be sent are worse than slow ones and are a refusal, not a wait: they
     * live in the old store's pending map, and swapping the object drops both them and the retry
     * that would have saved them, while the fresh store's `unsaved` is false — the warning would
     * disappear with nothing saved. The head's actions are disabled for the same reason.</p>
     *
     * @param andThen what to do once it has landed, in the words of the action he started — the
     *   sentence is read inside the box he opened.
     */
    private async _settleBeforeWrite(andThen: string): Promise<ProjectSaveGate> {
        const store = this._store;
        if (!store) return { ok: true, draftId: this._draftId };
        await store.whenQuiet();
        if (!store.unsaved) return { ok: true, draftId: this._draftId };
        return {
            ok: false,
            message: `Något du matat in har inte kunnat sparas ännu. Sidan försöker igen — ${andThen}`
                + ' när det har landat.',
        };
    }

    private _settleBeforeSave = (): Promise<ProjectSaveGate> =>
        this._settleBeforeWrite('spara i projekt');

    private _settleBeforeCopy = (): Promise<ProjectSaveGate> =>
        this._settleBeforeWrite('kopiera');

    /**
     * The box produced a calculation this page is now standing in — the draft moved into a project
     * ("Spara i projekt"), or a copy of the open one ("Kopiera till…"). One way in for both, so the
     * page has one mode switch and not two.
     *
     * <p>No navigation. After a move that is the point of a move: what the author typed is on
     * screen, it is in the stream, and the stream did not change. After a copy the stream IS
     * another one, so the page reads it — and it holds what the original folded to, which is what
     * is on screen. The address is rewritten either way, so a reload comes back to the calculation
     * he is in.</p>
     */
    private _onCalculationSaved(saved: ProjectCalculationSaved): void {
        const { calculation, projectName } = saved;
        this._picker = null;
        this._calculationRefused = false;
        this._draftRefused = false;
        this._calculationId = calculation.streamId;
        this._draftId = null;
        this._project = {
            id: calculation.projectId,
            name: projectName,
            calculationName: calculation.name,
            configuratorKey: calculation.configuratorKey,
            articles: [],
        };
        this._writeAddress();
        this._store = this._newStore();
        this._connectSSE();
        this._draftReady = this._adopt({});
    }

    /** What "Kopiera till…" copies, or null in draft mode — where there is no original to vary, and
     *  "Spara i projekt…" is the action instead (projekt-i-kalkylatorn 19 point 1). */
    private _copySource(): ProjectCopySource | null {
        const project = this._project;
        if (!project || !this._calculationId) return null;
        return {
            projectId: project.id,
            projectName: project.name,
            streamId: this._calculationId,
            calculationName: project.calculationName,
        };
    }

    /** The author chose another calculation: a navigation, because it is another node's preview as
     *  often as not — and the address is what carries the mode either way. */
    private _openCalculation(chosen: ProjectCalculationChosen): void {
        window.location.href = previewPageUrl(chosen.configuratorKey, chosen.streamId);
    }

    /** One of this node's drafts, from the arrow's list: a navigation, so the address carries it. */
    private _openDraft(draftId: string): void {
        window.location.href = previewPageUrl(this._contentId, null, draftId);
    }

    /**
     * "Ny kalkyl", in both modes: an empty page on this node that writes nothing until the first
     * input, which starts a draft of its own. The draft or project calculation the page stood in is
     * left exactly as it was (`configurator-preview-new-calculation.test.ts`).
     *
     * <p>Where it stands, not a navigation: an address without an id opens the LATEST draft, so there
     * is no address that means "empty". What the author did lands first, since the store that would
     * send it is replaced.</p>
     */
    private async _newCalculation(): Promise<void> {
        this._menuOpen = false;
        this._newCalculationRefused = null;
        const settled = await this._settleBeforeWrite('börja på en ny kalkyl');
        if (!settled.ok) { this._newCalculationRefused = settled.message; return; }

        this._calculationId = null;
        this._project = null;
        this._projectCalculations = [];
        this._calculationRefused = false;
        this._draftRefused = false;
        this._discardRefused = null;
        this._draftId = null;
        this._draftName = null;
        this._draftSummary = null;
        this._draftKnown = true;
        this._drafts = null;
        this._writeAddress();
        this._store = this._newStore();
        this._clearScreen();
        this._connectSSE();
    }

    /** What is on screen goes, and nothing is written: the store is where a write would come from. */
    private _clearScreen(): void {
        super._discardConfiguration();
        this._orphanedInputs = {};
        this._deferredFromServer = {};
        this._heldAssumptions = {};
        this._emptiedByTheAuthor.clear();
    }

    /** Whether the page holds anything "Ny kalkyl" would leave behind. */
    private _draftHasValues(): boolean {
        return hasValues(this.inputs) || hasValues(this._orphanedInputs);
    }

    /** The author's drafts on this node, for the arrow's list. */
    private async _loadDrafts(): Promise<void> {
        const drafts = await listDrafts(this._contentId);
        if (drafts) this._drafts = drafts;
    }

    /**
     * The summary line for what the page shows, stored when it differs from the server's.
     *
     * <p>Only from an evaluation of what is on screen — the dropdowns' texts are the evaluation's —
     * and only once the page knows the server's line, so a page that has not read its draft yet
     * cannot overwrite it with the empty form it starts with. A label, not evidence: a lost write is
     * not retried.</p>
     */
    private _storeSummaryIfChanged(): void {
        if (this.customerMode || this._calculationId || !this._draftId || !this._draftKnown) return;
        if (!this.state || !this._postedInputs || !sameInputs(this._postedInputs as DraftInputs, this.inputs)) return;
        const summary = draftSummary(this.schema, this.state, this.inputs);
        if (summary === this._draftSummary) return;
        this._draftSummary = summary;
        void storeDraftSummary(this._contentId, this._draftId, summary);
    }

    /** The author's own name for the draft, from the head; the head shows it once the server has it. */
    private async _renameDraft(name: string): Promise<void> {
        const draftId = this._draftId;
        if (!draftId) return;
        this._renaming = true;
        this._nameRefused = null;
        try {
            const result = await renameDraft(this._contentId, draftId, name);
            if (!result.ok) { this._nameRefused = result.message; return; }
            if (this._draftId === draftId) this._draftName = result.entry?.name ?? null;
        } finally {
            this._renaming = false;
        }
    }

    /**
     * "Släng kladden…", after its question: the draft leaves the author's list and the page goes to
     * the latest one left, or an empty page. A draft that owns an article number is refused, and
     * the refusal is shown with nothing on screen changed.
     */
    private async _discardDraft(): Promise<void> {
        this._menuOpen = false;
        this._discardRefused = null;
        const draftId = this._draftId;
        if (!draftId || this._discardAsking) return;
        this._discardAsking = true;
        const confirmed = await new Promise<boolean>((resolve) => { this._discardAnswer = resolve; });
        if (!confirmed || this._draftId !== draftId) return;

        await this._store?.whenQuiet();
        const result = await discardDraft(this._contentId, draftId);
        if (!result.ok && result.status !== 404) {
            this._discardRefused = result.message;
            return;
        }
        this._draftId = null;
        this._draftName = null;
        this._draftSummary = null;
        this._draftKnown = false;
        this._drafts = null;
        this._writeAddress();
        this._store = this._newStore();
        this._clearScreen();
        this._connectSSE();
        this._draftReady = this._adopt({});
        await this._draftReady;
    }

    /** Resolver for the question "Släng kladden…" asks, or null when none stands. */
    private _discardAnswer: ((confirmed: boolean) => void) | null = null;

    private _answerDiscard(confirmed: boolean): void {
        this._discardAsking = false;
        const answer = this._discardAnswer;
        this._discardAnswer = null;
        answer?.(confirmed);
    }

    /** The keys this surface no longer writes. Removed only after the hand-over has been made, so
     *  a failed transfer still has something to transfer next time. */
    private _forgetLocalInputs(): void {
        const key = this._storageKey;
        if (!key) return;
        try {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
            const orphansKey = this._orphanedStorageKey;
            if (orphansKey) localStorage.removeItem(orphansKey);
        } catch { /* private mode — nothing was stored to begin with */ }
    }

    /**
     * What the server says, onto the page.
     *
     * <p>Split against the schema exactly as a stored configuration is, and the leftovers REPLACE
     * the box's contents rather than merging into them: on this surface the stream is where a value
     * whose field is gone lives (the fold carries it), so the box is a view of the draft and not a
     * list of its own.</p>
     *
     * <p>The field the author is standing in keeps what he is typing — see
     * {@link _deferredFromServer}.</p>
     *
     * <p>The draft's assumptions are held with the value each was made of, and sent beside the inputs
     * while the field still holds it (`configurator-preview-assumed-choice.test.ts`).</p>
     */
    private _applyDraftInputs(inputs: DraftInputs, assumptions: DraftAssumptions = {}): void {
        const { kept, orphaned } = this._splitStoredInputs({ ...inputs }, this.schema);
        const sentBefore = this._assumptionsToSend();
        this._heldAssumptions = Object.fromEntries(Object.entries(assumptions)
            .filter(([alias]) => alias in kept)
            .map(([alias, assumed]) => [alias, { assumed, value: kept[alias] }]));
        this._emptiedByTheAuthor.clear();

        // Nothing moved: an evaluation here would be a request per signal on a page whose answers
        // are already the server's.
        if (sameInputs(kept, this.inputs) && sameInputs(orphaned, this._orphanedInputs)
            && JSON.stringify(assumptionsStillHeld(this._heldAssumptions, kept)) === JSON.stringify(sentBefore)) return;

        const focused = this._focusedAlias;
        if (focused && !sameInputValue(kept[focused], this.inputs[focused])) {
            this._deferredFromServer[focused] = {
                server: kept[focused],
                localAtDefer: this.inputs[focused],
            };
            if (this.inputs[focused] === undefined) delete kept[focused];
            else kept[focused] = this.inputs[focused];
        }

        this.inputs = kept;
        this._orphanedInputs = orphaned;
        this._scheduleEvaluate(0);
    }

    /**
     * The channel says the draft moved — whose work was it?
     *
     * <p>The sequence is the answer, but only once this page knows its own: the server notifies
     * BEFORE it answers the POST, so the signal about this page's own write normally arrives while
     * that write is still in flight, when the page's highest sequence is still the one before it.
     * Weighed then, every field the author leaves costs a read of what this very page just wrote.
     * So while a write is in flight the question waits for it, and is asked again against the
     * sequence the answer carried — a higher one is still somebody else's, and is read.</p>
     */
    private _onDraftSignal(sequence: unknown): void {
        const store = this._store;
        if (!store?.wantsReread(sequence)) return;
        if (!store.writing) { void this._rereadDraft(); return; }
        // Not awaited from inside the read chain: `_rereadDraft` appends to `_draftWork`, and a link
        // that waited for its own successor would never resolve.
        this._draftWork = Promise.allSettled([this._draftWork, store.whenQuiet()]).then(() => {
            if (store.wantsReread(sequence)) void this._rereadDraft();
        });
    }

    /** Another page moved the calculation: read it again and recompute. Queued behind whatever read
     *  is already running. */
    private _rereadDraft(): Promise<void> {
        const store = this._store;
        if (!store) return Promise.resolve();
        // A page with no draft has nothing to read again — without an id the read is the LATEST draft,
        // another tab's work — unless its first read never got through: then this is that read.
        if (!this._calculationId && !this._draftId) {
            if (this._draftKnown) return Promise.resolve();
            this._draftWork = this._draftWork.then(() => this._adopt({}), () => {});
            return this._draftWork;
        }
        this._draftWork = this._draftWork.then(async () => {
            const outcome = await store.read();
            if (outcome === 'loggedOut') { this._onSessionLost(); return; }
            if (outcome === 'unreachable') return;
            // The calculation has been lifted out of its project, or was never this author's. The
            // first read is where that is answered (`_fallBackToDraft`); a re-read leaves what is on
            // screen alone rather than blanking it under him.
            if (outcome === 'notFound') return;
            this._draftUnsaved = store.unsaved;
            // No stream is not an empty stream. A read that finds nothing is the answer for a
            // configurator nobody has written to, and applying it would blank the page.
            if (!outcome.streamId) return;
            if (this._project && outcome.project) this._project = { ...this._project, articles: outcome.project.articles };
            if (!this._calculationId) {
                this._draftName = outcome.name;
                this._draftSummary = outcome.summary;
                this._draftKnown = true;
            }
            this._applyDraftInputs(outcome.inputs, outcome.assumptions);
        }, () => {});
        return this._draftWork;
    }

    /** The author has finished with a field — one `InputChanged`, not one per character. The draft
     *  decides whether there is anything to say: a field tabbed through unanswered is not an edit
     *  and must not open a stream. */
    protected override _onFieldSettled(alias: string): void {
        if (this.customerMode) return;
        const decided = this._decidedByTheRun(alias);
        if (decided) {
            // The run's value is not the author's edit: it goes only into a draft that exists or is being
            // written (`configurator-preview-assumed-choice.test.ts`).
            const store = this._store;
            if (store && (store.hasStream || store.writing)) void store.setInput(alias, decided.value, decided.assumed);
            return;
        }
        delete this._heldAssumptions[alias];
        void this._store?.setInput(alias, this.inputs[alias]);
    }

    /**
     * Fields the author emptied himself in the calculation on screen. Blank, but answered: the run's value is
     * not written over his. It is a fact about that calculation, so a page that takes a draft's inputs, starts
     * a new calculation or empties this one starts it over (`configurator-preview-assumed-choice.test.ts`).
     */
    private _emptiedByTheAuthor = new Set<string>();

    protected override _onInputChange(alias: string, value: any, dataType: string = 'Text') {
        const had = this.inputs[alias];
        super._onInputChange(alias, value, dataType);
        if (!isBlank(this.inputs[alias])) this._emptiedByTheAuthor.delete(alias);
        else if (!isBlank(had)) this._emptiedByTheAuthor.add(alias);
    }

    /** What the page holds of the draft's assumptions: each with the value it was made of. */
    private _heldAssumptions: Record<string, HeldAssumption> = {};

    protected override _assumptionsToSend(): DraftAssumptions {
        return assumptionsStillHeld(this._heldAssumptions, this.inputs);
    }

    /**
     * The value the run settled for this field, and why, when the author has not answered it: the field is
     * blank on the page and he did not empty it himself, or it still holds the value the draft stored as
     * assumed. A field he answered is his, and is written without (`configurator-preview-assumed-choice.test.ts`).
     */
    private _decidedByTheRun(alias: string): { value: unknown; assumed: FieldAssumption } | null {
        const decided = assumedFields(this.state).find((field) => field.alias === alias);
        if (!decided) return null;
        const current = this.inputs[alias];
        const held = this._heldAssumptions[alias];
        const untouched = (isBlank(current) && !this._emptiedByTheAuthor.has(alias))
            || (held !== undefined && sameInputValue(held.value, current));
        return untouched ? decided : null;
    }

    /**
     * The run settled values the author has not answered: each is written to the draft as assumed, so a
     * later run can test it. Only from an evaluation of what is on screen, and only into a draft that
     * exists or is being written: a configurator merely looked at leaves no row behind
     * (`configurator-preview-assumed-choice.test.ts`).
     */
    private _storeAssumedChoices(): void {
        const store = this._store;
        if (!store || !(store.hasStream || store.writing)) return;
        if (!this.state || !this._postedInputs || !sameInputs(this._postedInputs as DraftInputs, this.inputs)) return;
        for (const { alias } of assumedFields(this.state)) {
            if (this._decidedByTheRun(alias)) this._onFieldSettled(alias);
        }
    }

    /**
     * Whether the field still holds what it held when the server's value arrived — the real question
     * behind "has he answered it himself since".
     *
     * <p>Numbers are compared as numbers, because leaving a numeric field NORMALISES what the
     * keystrokes left ('10' → 10) in the very same gesture: compared strictly, that reshaping reads
     * as an answer, the newer value is dropped and the old one is written back over it. Blank is
     * never numeric here — clearing a field IS an answer, and `Number('')` is 0.</p>
     */
    private static _unansweredSince(current: unknown, atDefer: unknown): boolean {
        if (sameInputValue(current, atDefer)) return true;
        if (current === '' || current == null || atDefer === '' || atDefer == null) return false;
        const a = Number(String(current).replace(',', '.'));
        const b = Number(String(atDefer).replace(',', '.'));
        return Number.isFinite(a) && Number.isFinite(b) && a === b;
    }

    /** Leaving a field is when a value that arrived meanwhile may be shown — if the author has not
     *  answered the field himself since it arrived, in which case his answer is the newer one. */
    protected override _onFieldBlur(alias: string): void {
        super._onFieldBlur(alias);

        const waiting = this._deferredFromServer[alias];
        if (!waiting) return;
        delete this._deferredFromServer[alias];
        if (!IxitConfiguratorPreviewStandalone._unansweredSince(this.inputs[alias], waiting.localAtDefer)) return;

        const next = { ...this.inputs };
        if (waiting.server === undefined) delete next[alias];
        else next[alias] = waiting.server;
        this.inputs = next;
        this._scheduleEvaluate(0);
    }

    /**
     * "Töm kalkylen…" in a project calculation throws the whole configuration away: the base clears
     * the keys, this clears what is on screen, and the calculation gets an empty dictionary, which the
     * fold applies wholesale. Backing past it comes with the history.
     */
    protected override _discardConfiguration(): void {
        super._discardConfiguration();
        this._orphanedInputs = {};
        this._deferredFromServer = {};
        this._heldAssumptions = {};
        this._emptiedByTheAuthor.clear();
        void this._store?.captureAll({});
    }

    private _connectSSE() {
        if (!this._contentId) return;
        this._eventSource?.close();

        // The generation travels on the FIRST subscribe and never again, so it is taken rather than
        // read. It is fixed at render time: sending it on every reconnect would have the server
        // answer schema-changed each time — a full schema fetch plus a re-evaluation, for as long as
        // the tab stays open, since the page's own value can never catch up (review 2026-09-15).
        // A reconnect needs no handshake anyway: it refetches on open because it was down.
        // A stream replaced before it opened never delivered the handshake, so its replacement carries
        // it instead (a page that learns its draft id while the first stream is still connecting).
        const generation = this._schemaGeneration ?? this._unspentGeneration;
        this._schemaGeneration = null;
        this._unspentGeneration = generation;
        const params = new URLSearchParams();
        if (generation !== null) params.set(SCHEMA_GENERATION_QUERY_KEY, String(generation));
        // Which calculation this page wants to be woken about: the project calculation or the draft
        // it stands in, and none on a page with no draft yet. The subscription follows the address,
        // so a page that changes what it stands in reconnects (`_onCalculationSaved`, `_onDraftNamed`).
        if (this._calculationId) params.set(CALCULATION_QUERY_KEY, this._calculationId);
        else if (this._draftId) params.set(DRAFT_QUERY_KEY, this._draftId);
        const query = params.toString();

        const es = new EventSource(previewApiUrl(this._contentId, `/events${query ? `?${query}` : ''}`));
        this._eventSource = es;

        es.onopen = () => {
            this._unspentGeneration = null;
            // A stream that comes BACK may have missed a signal while it was down; fetch once
            // rather than trust that nothing happened (den fristående previewn hör inte alltid en sparning — evalstack-integrity/56).
            const wasDown = this._sseStatus === 'reconnecting';
            this._sseStatus = 'connected';
            if (!wasDown) return;
            void this._refreshFromServer();
            // The calculation has the same hole and one more: a change of the author's that never
            // got out. Send it, then read what the other tabs did meanwhile.
            void this._store?.retryPending().then(() => this._rereadDraft());
        };

        es.onmessage = () => { void this._refreshFromServer(); };

        // A NAMED event — `es.onmessage` hears only the nameless ones, so the draft's signal needs
        // a listener of its own. Its sequence is the whole of "skip my own write": this page keeps
        // the highest number its own POSTs were answered with, and only a higher one is news.
        es.addEventListener('calculation', (event: Event) => {
            let sequence: unknown;
            try { sequence = JSON.parse((event as MessageEvent).data)?.sequence; }
            catch { return; }
            this._onDraftSignal(sequence);
        });

        es.onerror = () => {
            es.close();
            this._eventSource = null;
            if (this._sseStatus === 'loggedOut') return;
            this._sseStatus = 'reconnecting';
            // An EventSource cannot say WHY it failed: a dead session (302 → the login page, which
            // is not an event stream) and a network blip look the same to it, and reconnecting
            // every five seconds against a login page for ever was the "Återansluter…" the author
            // could not read (evalstack-integrity/57). So ask the session first, and reconnect only
            // while it is alive.
            void this._probeSession().then((alive) => {
                if (this._sseStatus === 'loggedOut') return;
                if (!alive) { this._onSessionLost(); return; }
                this._reconnectTimer = setTimeout(() => { this._reconnectTimer = null; this._connectSSE(); }, 5000);
            });
        };
    }

    /**
     * Whether the backoffice session behind this page is still alive — one request to the
     * controller's `session` action, which does nothing but be authorised: a dead session gets the
     * redirect, a live one a 204. Not the schema read: that projects every preset's thumbnail on
     * the server, and this question is asked every five seconds while a stream is down.
     */
    private _probeSession(): Promise<boolean> {
        if (this._sessionProbe) return this._sessionProbe;
        const probe = (async () => {
            try {
                const res = await fetch(previewApiUrl(this._contentId, '/session'), { redirect: 'manual' });
                return !IxitConfiguratorPreviewBase._isLoginRedirect(res);
            } catch {
                return true;   // a network error is not a logout; the stream's own retry handles it
            }
        })();
        this._sessionProbe = probe;
        // Cleared AFTER the assignment above, and only if it is still this probe: a fetch that fails
        // synchronously settles before the assignment, and a `finally` inside the function would
        // then have been undone by it — leaving a resolved "alive" cached for ever.
        void probe.finally(() => { if (this._sessionProbe === probe) this._sessionProbe = null; });
        return probe;
    }

    /** Logged out, and the author looked again: if the session is back, pick everything up. */
    private async _tryResumeSession(): Promise<void> {
        if (!(await this._probeSession())) return;
        this._onSessionBack();
        this._sseStatus = 'reconnecting';
        this._connectSSE();
        void this._refreshFromServer();
        void this._store?.retryPending().then(() => this._rereadDraft());
    }

    protected override _onSessionLost(fieldAlias: string | null = null): void {
        super._onSessionLost(fieldAlias);
        // Not the base's banner: the head row IS the banner here, and two would say it twice.
        this._evalError = null;
        this._sseStatus = 'loggedOut';
        this._eventSource?.close();
        this._eventSource = null;
        if (this._reconnectTimer) { clearTimeout(this._reconnectTimer); this._reconnectTimer = null; }
    }

    /** Opens the backoffice in a new tab, so this page — and what is typed into it — stays. */
    private _openLogin(): void {
        window.open('/umbraco', '_blank', 'noopener');
    }

    /**
     * Re-read the schema the server has now and re-evaluate against it, keeping the scroll
     * position — the one act behind every "something changed" signal: an SSE message, a stream
     * that reconnected, a tab that became visible again.
     */
    protected async _refreshFromServer(): Promise<void> {
        if (!this._contentId) return;
        // Coalesce: a second signal while one refresh runs (a message on the heels of a reconnect)
        // must not start a second fetch whose result would race the first by arrival. It queues ONE
        // more run after this one, whatever the number of signals.
        if (this._refreshInFlight) {
            this._refreshRequestedAgain = true;
            return this._refreshInFlight;
        }
        this._refreshInFlight = this._refreshOnce();
        try {
            await this._refreshInFlight;
        } finally {
            this._refreshInFlight = null;
            if (this._refreshRequestedAgain) {
                this._refreshRequestedAgain = false;
                void this._refreshFromServer();
            }
        }
    }

    private async _refreshOnce(): Promise<void> {
        const scrollY = window.scrollY;
        try {
            const res = await fetch(previewApiUrl(this._contentId, '?format=json'), { redirect: 'manual' });
            if (IxitConfiguratorPreviewBase._isLoginRedirect(res)) { this._onSessionLost(); return; }
            if (res.ok) {
                this.schema = await res.json();
                await this._evaluate();
                await this.updateComplete;
                window.scrollTo({ top: scrollY, behavior: 'instant' });
            }
        } catch {
            // network error during refresh — schema stays as-is
        }
    }

    render() {
        if (this.customerMode) {
            // The calculator, then the one thing to do with it. The form sits BELOW the whole
            // configuration on purpose: "begär offert" is what you do once you have configured
            // something, and a contact form above the product reads as a paywall.
            return html`
                ${super.render()}
                <ixit-quote-request-form
                    .configuratorId=${this._contentId}
                    .inputs=${this.inputs}
                    .issues=${this._customerIssues()}
                ></ixit-quote-request-form>
            `;
        }

        // The word is the status; the dot beside it only repeats it (colour never carries a state
        // alone here). Swedish, because the author reads Swedish everywhere else on this page.
        const statusLabel: Record<SseStatus, string> = {
            connected: 'Ansluten',
            reconnecting: 'Återansluter…',
            disconnected: 'Frånkopplad',
            loggedOut: 'Inloggningen i backoffice har gått ut.',
        };
        const loggedOut = this._sseStatus === 'loggedOut';
        return html`
            <div class="live-head ${loggedOut ? 'logged-out' : ''}" role=${loggedOut ? 'alert' : nothing}>
                <span class="head-group head-where">
                    ${this._renderStreamName()}
                    ${this._renderCalculationMenu()}
                    ${this._renderNewCalculationButton()}
                    ${this._project ? '' : html`
                        <uui-button
                            class="save-in-project"
                            look="secondary"
                            compact
                            label="Spara i projekt"
                            ?disabled=${this._draftUnsaved}
                            title=${this._draftUnsaved
                                ? 'Går inte medan något du matat in väntar på att sparas'
                                : 'Flyttar den här kladden till ett projekt, som en namngiven kalkyl'}
                            @click=${() => { this._picker = 'save'; this._menuOpen = false; }}
                        >Spara i projekt…</uui-button>
                    `}
                    ${this._project ? html`
                        <uui-button
                            class="copy-to-project"
                            look="secondary"
                            compact
                            label="Kopiera till"
                            ?disabled=${this._draftUnsaved}
                            title=${this._draftUnsaved
                                ? 'Går inte medan något du matat in väntar på att sparas'
                                : 'Sparar en variant av den här kalkylen, här eller i ett annat projekt'}
                            @click=${() => { this._picker = 'copy'; this._menuOpen = false; }}
                        >Kopiera till…</uui-button>
                    ` : ''}
                    <uui-button
                        class="open-project"
                        look="secondary"
                        compact
                        label="Öppna projekt"
                        title="Öppnar en kalkyl ur ett av dina projekt"
                        @click=${() => { this._picker = 'open'; this._menuOpen = false; }}
                    >Öppna projekt…</uui-button>
                </span>
                <span class="head-group head-state">
                    <span class="live-status ${this._sseStatus}" role="status" aria-live="polite">
                        <span class="live-dot" aria-hidden="true"></span>
                        <span>
                            ${statusLabel[this._sseStatus]}
                            ${loggedOut ? html` Det du matat in finns kvar här, men resultaten uppdateras
                                inte förrän du loggat in igen.` : ''}
                        </span>
                    </span>
                    ${loggedOut ? html`
                        <uui-button look="primary" color="default" label="Logga in igen" @click=${this._openLogin}>
                            Logga in igen
                        </uui-button>
                    ` : ''}
                    ${this._draftUnsaved ? html`
                        <span class="draft-unsaved" role="status">
                            <span aria-hidden="true">⚠</span>
                            <span>Kunde inte spara — försöker igen när kontakten är tillbaka.</span>
                        </span>
                    ` : ''}
                </span>
                <span class="head-group head-view">
                    <ixit-preview-view-menu>
                        <span class="view-menu-controls">
                            ${this._renderViewControls()}
                            <span class="live-links" title="Kräver inloggning i backoffice">
                                <a href="${previewApiUrl(this._contentId, '/debug/source?inline')}" target="_blank" rel="noopener">👁 Visa källschema</a>
                                <a href="${previewApiUrl(this._contentId, '/debug/source')}" download>⬇ Ladda ner (JSON)</a>
                            </span>
                        </span>
                    </ixit-preview-view-menu>
                </span>
            </div>
            ${this._nameRefused ? html`
                <div class="name-refused" role="alert">
                    <span aria-hidden="true">⚠</span><span>${this._nameRefused}</span>
                </div>
            ` : ''}
            ${this._calculationRefused ? html`
                <div class="calculation-refused" role="alert">
                    <span aria-hidden="true">⚠</span>
                    <span>Kalkylen i adressen finns inte, eller hör till någon annan. Du ser din
                        senaste kladd för den här noden.</span>
                </div>
            ` : ''}
            ${this._draftRefused ? html`
                <div class="calculation-refused draft-refused" role="alert">
                    <span aria-hidden="true">⚠</span>
                    <span>Kladden i adressen finns inte längre, eller hör till någon annan. Du ser din
                        senaste kladd för den här noden.</span>
                </div>
            ` : ''}
            ${this._newCalculationRefused ? html`
                <div class="new-calculation-refused" role="alert">
                    <span aria-hidden="true">⚠</span><span>${this._newCalculationRefused}</span>
                </div>
            ` : ''}
            ${this._discardRefused ? html`
                <div class="new-calculation-refused discard-refused" role="alert">
                    <span aria-hidden="true">⚠</span><span>${this._discardRefused}</span>
                </div>
            ` : ''}
            ${this._discardAsking ? html`
                <ixit-restart-confirm-dialog
                    class="discard-draft-confirm"
                    .prompt=${DISCARD_DRAFT_PROMPT}
                    @restart-answer=${(e: CustomEvent) => this._answerDiscard(!!e.detail?.confirmed)}
                ></ixit-restart-confirm-dialog>
            ` : ''}
            ${this._picker ? html`
                <ixit-project-picker
                    .mode=${this._picker}
                    .nodeKey=${this._contentId}
                    .nodeName=${(this.schema?.name || '').trim()}
                    .suggestedName=${this._draftName ?? this._draftSummary ?? ''}
                    .copySource=${this._copySource()}
                    .beforeSave=${this._picker === 'copy' ? this._settleBeforeCopy : this._settleBeforeSave}
                    @project-calculation-saved=${(e: CustomEvent<ProjectCalculationSaved>) =>
                        this._onCalculationSaved(e.detail)}
                    @project-calculation-open=${(e: CustomEvent<ProjectCalculationChosen>) =>
                        this._openCalculation(e.detail)}
                    @project-picker-close=${() => { this._picker = null; }}
                ></ixit-project-picker>
            ` : ''}
            ${this._renderOrphanedInputs()}
            ${super.render()}
        `;
    }

    /**
     * Where the author is standing, in words and in a glyph — never in colour alone: the project and
     * the calculation's name when the address names one, and "Kladd" for this node when it does not.
     *
     * <p>Every name is changed where it stands (projekt-i-kalkylatorn 18 point 6): the project's and
     * the calculation's, and a draft's own name once the draft exists — shown by its summary until
     * the author gives it one.</p>
     *
     * <p><b>The project editor renders for everyone the page lets in</b>: renaming a project is
     * `ProjectAccessLevel.Change`, which everybody who may open one of its calculations has.</p>
     */
    private _renderStreamName() {
        const nodeName = (this.schema?.name || '').trim();
        if (this._project) {
            return html`
                <span class="live-title" data-mode="project">
                    <span aria-hidden="true">▣</span>
                    <ixit-inline-name-editor
                        class="project-name"
                        label="Projektets namn"
                        .value=${this._project.name}
                        ?busy=${this._renaming}
                        @name-commit=${(e: CustomEvent<{ value: string }>) =>
                            void this._renameProject(e.detail.value)}
                    ></ixit-inline-name-editor>
                    <span aria-hidden="true">›</span>
                    <ixit-inline-name-editor
                        class="calculation-name"
                        label="Kalkylens namn"
                        .value=${this._project.calculationName}
                        ?busy=${this._renaming}
                        @name-commit=${(e: CustomEvent<{ value: string }>) =>
                            void this._renameCalculation(e.detail.value)}
                    ></ixit-inline-name-editor>
                </span>
            `;
        }
        return html`
            <span class="live-title" data-mode="draft">✎ Kladd${nodeName ? ` · ${nodeName}` : ''}${this._draftId ? html`
                <span aria-hidden="true">›</span>
                <ixit-inline-name-editor
                    class="draft-name"
                    label="Kladdens namn"
                    .value=${this._draftName ?? this._draftSummary ?? 'Namnlös'}
                    ?busy=${this._renaming}
                    @name-commit=${(e: CustomEvent<{ value: string }>) => void this._renameDraft(e.detail.value)}
                ></ixit-inline-name-editor>` : html` <span class="draft-new">· ny</span>`}</span>
        `;
    }

    /**
     * The author's drafts on this node, most recently written first: its own name before its summary,
     * then when it was last written. The draft the page stands in is marked and not a link.
     */
    private _renderDraftList() {
        const drafts = this._drafts;
        if (drafts === null) {
            return html`<span class="calc-menu-item draft-list-loading" role="menuitem" aria-disabled="true">Läser dina kladdar…</span>`;
        }
        if (drafts.length === 0) {
            return html`<span class="calc-menu-item draft-list-empty" role="menuitem" aria-disabled="true">Inga kladdar på den här noden</span>`;
        }
        return drafts.map((draft) => html`
            <button
                class="calc-menu-item draft-item"
                role="menuitem"
                type="button"
                data-draft-id=${draft.streamId}
                ?disabled=${draft.streamId === this._draftId}
                @click=${() => this._openDraft(draft.streamId)}
            >
                ✎ ${draft.name || draft.summary || 'Namnlös kladd'}
                <span class="draft-when"> · ${draftWhen(draft.lastWrittenAt)}</span>
            </button>
        `);
    }

    /**
     * The calculation's new name, as the author typed it.
     *
     * <p>What is on screen is only moved once the server has accepted it: a name that was refused
     * must not sit in the head row looking saved. The refusal is the server's own sentence, and the
     * editor puts the old name back by itself when `_project` is unchanged.</p>
     */
    private async _renameCalculation(name: string): Promise<void> {
        const project = this._project;
        if (!project || !this._calculationId) return;
        this._renaming = true;
        this._nameRefused = null;
        try {
            const result = await renameCalculation(project.id, this._calculationId, name);
            if (!result.ok) { this._nameRefused = result.message; return; }
            this._project = { ...project, calculationName: result.value.name };
            await this._loadProjectCalculations();
        } finally {
            this._renaming = false;
        }
    }

    /** The project's new name. Same shape as the calculation's; the endpoint behind it is the one
     *  the project list uses (`projects-client.renameProject`). */
    private async _renameProject(name: string): Promise<void> {
        const project = this._project;
        if (!project) return;
        this._renaming = true;
        this._nameRefused = null;
        try {
            const result = await renameProject(project.id, name);
            if (!result.ok) { this._nameRefused = result.message; return; }
            this._project = { ...project, name: result.value.name };
        } finally {
            this._renaming = false;
        }
    }

    /**
     * The arrow beside the name: the author's drafts on this node, then — in project mode — the
     * project's calculations.
     *
     * <p>The act that throws something away is the menu's last row, set apart from the list, so it is
     * never a button in the row: "Töm kalkylen…" for a project calculation, "Släng kladden…" for a
     * draft that exists.</p>
     */
    private _renderCalculationMenu() {
        return html`
            <span class="calc-menu">
                <uui-button
                    class="calc-menu-toggle"
                    look="secondary"
                    compact
                    label=${this._project ? 'Byt kalkyl' : 'Dina kladdar'}
                    aria-expanded=${this._menuOpen ? 'true' : 'false'}
                    @click=${() => {
                        this._menuOpen = !this._menuOpen;
                        if (this._menuOpen) void this._loadDrafts();
                    }}
                >▾</uui-button>
                ${this._menuOpen ? html`
                    <span class="calc-menu-list" role="menu">
                        ${this._renderDraftList()}
                        ${this._project ? this._projectCalculations.map((calc) => html`
                            <button
                                class="calc-menu-item"
                                role="menuitem"
                                type="button"
                                data-stream-id=${calc.streamId}
                                ?disabled=${calc.streamId === this._calculationId}
                                @click=${() => this._openCalculation(calc)}
                            >
                                ▣ ${calc.name}${calc.configuratorKey === this._contentId
                                    ? '' : ' (annan nod)'}
                            </button>
                        `) : ''}
                        ${this._project ? html`
                            <span class="calc-menu-separator" role="separator"></span>
                            <button class="calc-menu-item calc-menu-clear restart-link" role="menuitem" type="button"
                                    title="Tömmer kalkylen — allt du matat in i den"
                                    @click=${() => { this._menuOpen = false; void this._startOver(); }}>
                                Töm kalkylen…
                            </button>
                        ` : this._draftId ? html`
                            <span class="calc-menu-separator" role="separator"></span>
                            <button class="calc-menu-item calc-menu-discard" role="menuitem" type="button"
                                    title="Tar bort kladden du står i ur din lista"
                                    @click=${() => { void this._discardDraft(); }}>
                                Släng kladden…
                            </button>
                        ` : ''}
                    </span>
                ` : ''}
            </span>
        `;
    }

    /**
     * "Ny kalkyl", in both modes. It waits, like the buttons beside it, for what the author typed to
     * reach the server; on a page that is already new and empty there is nothing to leave.
     */
    private _renderNewCalculationButton() {
        const alreadyNew = !this._project && !this._draftId && !this._draftHasValues();
        const disabled = alreadyNew || this._draftUnsaved;
        const title = alreadyNew
            ? 'Du står redan i en ny, tom kalkyl'
            : this._draftUnsaved
                ? 'Går inte medan något du matat in väntar på att sparas'
                : this._project
                    ? 'Börjar på en tom kalkyl. Kalkylen i projektet lämnas som den är.'
                    : 'Börjar på en tom kalkyl. Kladden du står i lämnas som den är och finns kvar under ▾.';
        return html`
            <uui-button
                class="new-calculation"
                look="primary"
                compact
                label="Ny kalkyl"
                ?disabled=${disabled}
                title=${title}
                @click=${() => { void this._newCalculation(); }}
            >Ny kalkyl</uui-button>
        `;
    }

    /**
     * The saved values with nowhere to belong, above the form — the author's surface only, since
     * the customer branch of `render` returns before this.
     */
    private _renderOrphanedInputs() {
        const entries: OrphanedInput[] = Object.entries(this._orphanedInputs)
            .map(([alias, value]) => ({ alias, value }));
        if (entries.length === 0) return nothing;

        return html`
            <ixit-orphaned-inputs-box
                .entries=${entries}
                .targets=${this._orphanTargets()}
                @orphan-move=${(e: CustomEvent) => this._moveOrphan(e.detail.alias, e.detail.target)}
                @orphan-discard=${(e: CustomEvent) => this._forgetOrphan(e.detail.alias)}
            ></ixit-orphaned-inputs-box>
        `;
    }

    /**
     * The engine's own verdicts about this configuration, as the public contract carries them: a
     * machine code, the standard customer sentence, and whether it blocks.
     *
     * Nothing rendered them before — the contract kept them (issue 02) and the author's preview has
     * its own, louder surfaces for the same faults. They belong on the CUSTOMER's page for two
     * reasons: a customer looking at a number the engine does not trust must be told, and a quote
     * request carrying such a configuration would arrive at DOF looking like any other, and be
     * answered with a price.
     */
    private _customerIssues(): QuoteBlockingIssue[] {
        // The one property of the evaluated state this component reads, named — rather than `as any`,
        // which would let a rename of `issues` compile straight through to an empty list on a page
        // that then quotes a number the engine does not trust.
        const state = this.state as { issues?: QuoteBlockingIssue[] } | null;
        return state?.issues ?? [];
    }
}

declare global {
  interface HTMLElementTagNameMap {
    'ixit-configurator-preview-standalone': IxitConfiguratorPreviewStandalone;
  }
}
